import React, { useState } from 'react';
import './StatusIndicator.css';

/**
 * StatusIndicator Component
 * 
 * Provides real-time visibility of system status, adhering to the MIMBCD-UI principle
 * of "visibility of system status" to keep users informed about what's happening.
 * 
 * 
 * @param {Object} props - Component props
 * @param {string} props.status - Overall status ('loading', 'ready', 'error', 'partial')
 * @param {Object} props.loadingDetails - Detailed loading state for each data source
 */
const StatusIndicator = ({ status, loadingDetails }) => {
  const [showDetails, setShowDetails] = useState(false);

  // Get status icon and color
  const getStatusDisplay = () => {
    switch (status) {
      case 'ready':
        return {
          icon: '✓',
          text: 'Ready',
          color: '#00a855',
          className: 'status-ready'
        };
      case 'loading':
        return {
          icon: '⟳',
          text: 'Loading',
          color: '#ffa500',
          className: 'status-loading'
        };
      case 'error':
        return {
          icon: '⚠',
          text: 'Error',
          color: '#ff6b6b',
          className: 'status-error'
        };
      case 'partial':
        return {
          icon: '◐',
          text: 'Partial',
          color: '#ffcc00',
          className: 'status-partial'
        };
      default:
        return {
          icon: '○',
          text: 'Unknown',
          color: '#888888',
          className: 'status-unknown'
        };
    }
  };

  // Get detail status for each data source
  const getDetailStatus = (detailStatus) => {
    switch (detailStatus) {
      case 'success':
        return { icon: '✓', text: 'Loaded', className: 'detail-success' };
      case 'loading':
        return { icon: '⟳', text: 'Loading...', className: 'detail-loading' };
      case 'error':
        return { icon: '✗', text: 'Unavailable', className: 'detail-error' };
      case 'no-patient':
        return { icon: '−', text: 'No patient', className: 'detail-empty' };
      default:
        return { icon: '?', text: 'Unknown', className: 'detail-unknown' };
    }
  };

  const statusDisplay = getStatusDisplay();

  return (
    <div className="status-indicator-container">
      {/* Main Status Badge */}
      <button
        className={`status-indicator ${statusDisplay.className}`}
        onClick={() => setShowDetails(!showDetails)}
        title="Click for detailed status"
      >
        <span className="status-icon" style={{ color: statusDisplay.color }}>
          {statusDisplay.icon}
        </span>
        <span className="status-text">{statusDisplay.text}</span>
      </button>

      {/* Detailed Status Tooltip */}
      {showDetails && (
        <div className="status-details-tooltip">
          <div className="tooltip-header">
            <h5>System Status</h5>
            <button
              className="close-tooltip-btn"
              onClick={(e) => {
                e.stopPropagation();
                setShowDetails(false);
              }}
            >
              ×
            </button>
          </div>

          <div className="tooltip-content">
            {/* Overall Status */}
            <div className="status-item overall">
              <span className="item-label">Overall:</span>
              <span className={`item-status ${statusDisplay.className}`}>
                {statusDisplay.icon} {statusDisplay.text}
              </span>
            </div>

            <div className="status-divider"></div>

            {/* Detailed Breakdown */}
            <div className="status-breakdown">
              <div className="breakdown-header">Data Sources:</div>

              {loadingDetails && Object.entries(loadingDetails).map(([key, value]) => {
                const detailStatus = getDetailStatus(value);
                return (
                  <div key={key} className="status-item detail">
                    <span className="item-label">
                      {formatLabel(key)}:
                    </span>
                    <span className={`item-status ${detailStatus.className}`}>
                      {detailStatus.icon} {detailStatus.text}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Connection Status */}
            <div className="status-divider"></div>
            <div className="status-item connection">
              <span className="item-label">Backend:</span>
              <span className="item-status detail-success">
                ✓ Connected
              </span>
            </div>

            {/* Timestamp */}
            <div className="status-timestamp">
              <small>Last updated: {new Date().toLocaleTimeString()}</small>
            </div>
          </div>

          {/* Status Legend (for clarity) */}
          <div className="status-legend">
            <small className="legend-title">Status Legend:</small>
            <div className="legend-items">
              <span className="legend-item">
                <span className="legend-icon status-ready">✓</span>
                <small>Ready</small>
              </span>
              <span className="legend-item">
                <span className="legend-icon status-loading">⟳</span>
                <small>Loading</small>
              </span>
              <span className="legend-item">
                <span className="legend-icon status-error">✗</span>
                <small>Unavailable</small>
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to format data source labels
const formatLabel = (key) => {
  const labels = {
    'diagnostic': 'Diagnostic Data',
    'contextual': 'Contextual Data',
    'messages': 'Message Templates',
    'findings': 'Findings Data'
  };
  return labels[key] || key.charAt(0).toUpperCase() + key.slice(1);
};

export default StatusIndicator;
